/**
 * `/settings/workspace/devices` — the owner's approval surface for headless
 * device enrollment (lane APPROVESURFACE).
 *
 * THE PROPERTY THIS PAGE EXISTS TO ESTABLISH. A machine with no browser can
 * only reach verification authority through a human's recorded consent. That
 * human step is the rail, not the bug — a credential a script could mint would
 * defeat the whole system. What was wrong was its COST: consenting required
 * shell access and a current session credential in a 0600 file on disk. This
 * page makes the same decision, by the same authority, take seconds on a phone.
 *
 * It is a THIN SHELL and must stay one: every decision is made server-side by
 * `decideDeviceEnrollmentApproval`. A reader who wants to know what approval
 * MEANS should read `packages/core/src/verification/device-enrollment.ts`, not
 * this file. The presentation lives in `./settings-device-enrollment-view`;
 * that file's header explains why the split is load-bearing for proof.
 *
 * WHAT IS DELIBERATELY ABSENT.
 *
 *  · No decline. The consent table is append-only by trigger and offers exactly
 *    one terminal transition — consent — so "declined" is not representable
 *    without a schema change, and `recordDeviceEnrollmentApproval` (which this
 *    surface must not fork) would not consult a new column anyway. Not
 *    approving IS the decline: a pending request expires on its own, within 15
 *    minutes by default and an hour at the ceiling. See
 *    `docs/verification/HEADLESS-ENROLLMENT.md`.
 *  · No "approve all". Consent is per-device and per-scope by construction.
 *  · No workspace picker. The tenant is the one the caller's own session
 *    resolves to; it is never sent by the browser.
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import { apiCall, useApi } from "@excellent/ui/api";
import {
  deviceEnrollment,
  type DeviceEnrollmentRequestView,
} from "@excellent/ui/api/contracts/device-enrollment";
import { DeviceApprovalsView } from "./settings-device-enrollment-view";
import { rootRoute } from "./__root";

/** Container — the only place this page talks to the network, and it does so
 * through the contract, never `fetch`. */
function DeviceApprovals() {
  const { data, isLoading, error, mutate } = useApi(deviceEnrollment.requests, {});
  const [busyRequestId, setBusyRequestId] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [refusal, setRefusal] = React.useState<string | null>(null);

  async function onApprove(
    request: DeviceEnrollmentRequestView,
    scopes: string[],
  ): Promise<void> {
    setBusyRequestId(request.requestId);
    setNotice(null);
    setRefusal(null);
    try {
      const result = await apiCall(deviceEnrollment.approve, {
        body: { userCode: request.userCode, scopes },
      });
      setNotice(
        `${result.deviceLabel} may now enroll with ${result.approvedScopes.join(", ")}. ` +
          "It still has to present its own device secret to redeem.",
      );
      await mutate();
    } catch (err) {
      setRefusal(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyRequestId(null);
    }
  }

  return (
    <DeviceApprovalsView
      workspaceId={data?.workspaceId ?? null}
      enrollments={data?.enrollments ?? []}
      isLoading={isLoading}
      error={refusal ?? (error ? (error.message ?? String(error)) : null)}
      notice={notice}
      busyRequestId={busyRequestId}
      onApprove={onApprove}
    />
  );
}

export const settingsWorkspaceDevicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/devices",
  component: DeviceApprovals,
});
